import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Ng4LoadingSpinnerService } from "ng4-loading-spinner";
import { MatDialog } from "@angular/material";
import * as moment from "moment";
import { NotificationsService } from "angular2-notifications";
import { ColDef, GridOptions, GridApi, ColumnApi } from "ag-grid-community";
import { UserService } from "../services/user.service";
import { OfframpService } from "../services/offramp.service";
import { ProperJsonDialogComponent } from "../properJsonDialog/properjsondialog.component";
import { TableUtil } from "../admintrademaster/tableUtil";
import { amountFormatter } from "../custompipes/amount-formatter";

@Component({
  selector: "app-payout-ledger",
  templateUrl: "./payout-ledger.component.html",
  styleUrls: ["./payout-ledger.component.css"],
})
export class PayoutLedgerComponent implements OnInit {
  _csrf: string;

  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;
  gridOptions: GridOptions;
  columnDefs: ColDef[] = [];
  rowData: any[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
  };

  ledger: any[] = [];
  searchValue: string = "";
  filter: any = this.getDefaultFilter();
  prev_disabled = true;
  next_disabled = false;

  entryTypes: string[] = ["TOPUP", "PAYOUT_DEBIT", "PAYOUT_REVERSAL", "ADJUSTMENT"];

  userList: any[] = [];
  filteredUserList: any[] = [];
  userSearchValue: string = "";

  totalCount: number = 0;
  totalCredit: number = 0;
  totalDebit: number = 0;
  totalReversal: number = 0;
  netAmount: number = 0;

  myFilter = (d: Date | null): boolean => true;

  constructor(
    private route: ActivatedRoute,
    private notifSer: NotificationsService,
    private userService: UserService,
    private offrampService: OfframpService,
    private spinnerService: Ng4LoadingSpinnerService,
    public dialog: MatDialog
  ) {
    this.initializeGrid();
  }

  ngOnInit() {
    // Opened from the Payout Wallet page's "View Ledger" action
    const userId = this.route.snapshot.queryParamMap.get("userId");
    if (userId) {
      this.filter.userId = userId;
    }
    this.csrf();
  }

  getDefaultFilter() {
    return {
      page: 1,
      limit: 100,
      fromDate: moment().format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      userId: "",
      entryType: "",
    };
  }

  loadUserList() {
    if (!this._csrf) {
      return;
    }

    const filter = {
      _csrf: this._csrf,
      perpage: 10000,
      page: 1,
      search: "",
      status: "Active",
    };

    this.offrampService.getOfframpCustomerList(filter).subscribe(
      (response: any) => {
        if (response && response.customers && Array.isArray(response.customers)) {
          this.userList = response.customers;
        } else if (response && Array.isArray(response)) {
          this.userList = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          this.userList = response.data;
        } else {
          this.userList = [];
        }
        this.filteredUserList = this.userList;
      },
      () => {
        this.userList = [];
        this.filteredUserList = [];
      }
    );
  }

  initializeGrid() {
    this.columnDefs = [
      {
        headerName: "Action",
        field: "action",
        cellRenderer: (params) => {
          const element = params.data;
          const viewBtn = document.createElement("button");
          viewBtn.type = "button";
          viewBtn.className = "btn btn-sm btn-info";
          viewBtn.title = "View";
          viewBtn.innerHTML = '<i class="fa fa-eye"></i>';
          viewBtn.onclick = (e) => {
            e.stopPropagation();
            this.displayProperJson(element, "Payout Ledger");
          };
          return viewBtn;
        },
        sortable: false,
        filter: false,
        flex: 0.7,
        minWidth: 80,
      },
      {
        headerName: "Date",
        field: "createdAt",
        valueFormatter: (params) => {
          if (params.value) {
            return moment(params.value).format("MMM DD, YYYY hh:mm:ss A");
          }
          return "";
        },
        flex: 1.5,
      },
      {
        headerName: "User",
        field: "UserId.name",
        valueGetter: (params) => {
          if (params.data && params.data.UserId && params.data.UserId.name) {
            return params.data.UserId.name;
          }
          return "";
        },
        flex: 1.5,
      },
      {
        headerName: "Txn Type",
        field: "TxnType",
        flex: 1,
        cellStyle: (params) => {
          if (params.value === "CREDIT") return { background: "#e6ffe6" };
          if (params.value === "DEBIT") return { background: "#ffe6e6" };
          return null;
        },
      },
      { headerName: "Entry Type", field: "EntryType", flex: 1.2 },
      { headerName: "Amount", field: "Amount", flex: 1, valueFormatter: amountFormatter },
      { headerName: "Opening Balance", field: "OpeningBalance", flex: 1, valueFormatter: amountFormatter },
      { headerName: "Closing Balance", field: "ClosingBalance", flex: 1, valueFormatter: amountFormatter },
      { headerName: "Currency", field: "Currency", flex: 0.8 },
      { headerName: "Payout Order Id", field: "PayoutOrderId", flex: 1.5 },
      { headerName: "Ref Id", field: "RefId", flex: 1.5 },
      { headerName: "Note", field: "Note", flex: 1.5 },
      { headerName: "Entry By", field: "EntryBy", flex: 1 },
    ];

    this.gridOptions = {
      columnDefs: this.columnDefs,
      defaultColDef: this.defaultColDef,
      rowData: this.rowData,
      pagination: false,
      suppressHorizontalScroll: false,
      enableRangeSelection: true,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      animateRows: true,
      rowHeight: 40,
      headerHeight: 40,
    };
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.sizeColumnsToFit();
  }

  csrf() {
    this.userService.csrf().subscribe(
      (success) => {
        this._csrf = success._csrf;
        this.loadUserList();
        this.getData();
      },
      (error) => {
        this.notifSer.error("Error", error.error.message, {
          timeOut: 3000,
          showProgressBar: true,
          pauseOnHover: false,
          clickToClose: true,
          maxLength: 50,
        });
      }
    );
  }

  search() {
    this.filter.page = 1;
    this.getData();
  }

  goNext() {
    this.filter.page++;
    this.getData();
  }

  goPrev() {
    if (this.filter.page > 1) {
      this.filter.page--;
      this.getData();
    }
  }

  fromDateFilter(event: any) {
    if (event.value) {
      this.filter["fromDate"] = moment(event.value).format("YYYY-MM-DD");
    }
  }

  toDateFilter(event: any) {
    if (event.value) {
      this.filter["toDate"] = moment(event.value).format("YYYY-MM-DD");
    }
  }

  applyFilter(filterValue) {
    this.searchValue = filterValue || "";
    if (this.gridApi) {
      this.gridApi.setQuickFilter(this.searchValue);
    }
  }

  clearSearch() {
    this.searchValue = "";
    this.applyFilter("");
  }

  getData() {
    this.spinnerService.show();
    this.filter._csrf = this._csrf;
    this.userService.getAllPayoutLedger(this.filter).subscribe(
      (list: any) => {
        this.ledger = list || [];
        this.rowData = this.ledger;

        if (this.gridApi) {
          this.gridApi.setRowData(this.rowData);
          this.gridApi.setQuickFilter(this.searchValue || "");
        }

        this.spinnerService.hide();
        this.prev_disabled = this.filter.page <= 1;
        this.next_disabled = this.ledger.length < this.filter.limit;
      },
      () => {
        this.notifSer.error("Error", "Failed to load payout ledger", {
          timeOut: 3000,
          showProgressBar: true,
          pauseOnHover: false,
          clickToClose: true,
          maxLength: 50,
        });
        this.spinnerService.hide();
      }
    );

    this.userService.getPayoutLedgerSummary(this.filter).subscribe((response: any) => {
      this.totalCount = response && response.totalCount ? response.totalCount : 0;
      this.totalCredit = response && response.totalCredit ? response.totalCredit : 0;
      this.totalDebit = response && response.totalDebit ? response.totalDebit : 0;
      this.totalReversal = response && response.totalReversal ? response.totalReversal : 0;
      this.netAmount = response && response.netAmount ? response.netAmount : 0;
    });
  }

  exportArray() {
    this.spinnerService.show();

    // Fetch fresh with a large limit (ignoring current page) so the export isn't capped to one page
    const exportFilter = {
      _csrf: this._csrf,
      fromDate: this.filter.fromDate,
      toDate: this.filter.toDate,
      userId: this.filter.userId || "",
      entryType: this.filter.entryType || "",
      page: 1,
      limit: 5000,
    };

    this.userService.getAllPayoutLedger(exportFilter).subscribe(
      (list: any) => {
        if (list && list.length > 0) {
          const exportData = list.map((x: any) => ({
            ...x,
            UserId: x.UserId ? x.UserId._id : "",
            UserName: x.UserId ? x.UserId.name : "",
          }));
          TableUtil.exportArrayToExcel(exportData, "PayoutLedger");
        } else {
          this.notifSer.info("Info", "No data available to export", {
            timeOut: 3000,
            showProgressBar: true,
            pauseOnHover: false,
            clickToClose: true,
            maxLength: 50,
          });
        }
        this.spinnerService.hide();
      },
      () => {
        this.notifSer.error("Error", "Failed to export data. Please try again.", {
          timeOut: 3000,
          showProgressBar: true,
          pauseOnHover: false,
          clickToClose: true,
          maxLength: 50,
        });
        this.spinnerService.hide();
      }
    );
  }

  displayProperJson(jsonData, eventType) {
    const obj = {
      jsonData: jsonData,
      eventType: eventType || "Payout Ledger",
    };
    this.dialog
      .open(ProperJsonDialogComponent, {
        width: "auto",
        height: "auto",
        data: obj,
      })
      .afterClosed()
      .subscribe(() => { });
  }

  resetFilters() {
    this.filter = this.getDefaultFilter();
    this.searchValue = "";
    this.userSearchValue = "";
    this.filteredUserList = this.userList;
    if (this.gridApi) {
      this.gridApi.setQuickFilter("");
    }
    this.getData();
  }

  onUserSelectOpened(isOpen: boolean) {
    this.userSearchValue = "";
    this.filteredUserList = this.userList;
    if (isOpen) {
      setTimeout(() => {
        this.addSearchToSelectPanel();
      }, 150);
    }
  }

  addSearchToSelectPanel() {
    const panel = document.querySelector(".user-select-panel-payout-ledger-list");
    if (panel) {
      const existingSearch = panel.querySelector(".user-search-container");
      if (existingSearch) {
        existingSearch.remove();
      }

      const searchContainer = document.createElement("div");
      searchContainer.className = "user-search-container";
      searchContainer.innerHTML = `
        <input type="text" placeholder="Search user..." class="user-search-input" value="${this.userSearchValue || ""}">
      `;

      const searchInput = searchContainer.querySelector(".user-search-input") as HTMLInputElement;
      if (searchInput) {
        searchInput.addEventListener("input", (e: any) => {
          this.userSearchValue = e.target.value;
          this.filterUsers(e);
        });
        searchInput.addEventListener("click", (e: any) => {
          e.stopPropagation();
        });
        searchInput.addEventListener("keydown", (e: any) => {
          e.stopPropagation();
        });
      }

      if (panel.firstChild) {
        panel.insertBefore(searchContainer, panel.firstChild);
      } else {
        panel.appendChild(searchContainer);
      }

      if (searchInput) {
        setTimeout(() => {
          searchInput.focus();
        }, 50);
      }
    }
  }

  filterUsers(event: any) {
    const searchTerm = event ? (event.target ? event.target.value : event) : this.userSearchValue;
    this.userSearchValue = searchTerm || "";

    if (!searchTerm || searchTerm.trim() === "") {
      this.filteredUserList = this.userList;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredUserList = this.userList.filter((user) => {
      const name = (user.name || "").toLowerCase();
      const email = (user.email || "").toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/payout-ledger/payout-ledger.component.ts